import { HDate, HebrewCalendar, Location, Zmanim, calculateMolad, flags, getMoladAsDate, months } from "@hebcal/core";
import { getLeyningOnDate, makeLeyningSummary } from "@hebcal/leyning";
import tzLookup from "tz-lookup";

const PERIODS = {
  morning: {
    id: "morning",
    group: "shacharit",
    title: "Shacharit",
    hebrew: "שַׁחֲרִית",
    section: "morning-blessings",
  },
  afternoon: {
    id: "afternoon",
    group: "minchah",
    title: "Minchah",
    hebrew: "תְּפִלַּת מִנְחָה",
    section: "minchah-weekday",
  },
  evening: {
    id: "evening",
    group: "maariv",
    title: "Maariv",
    hebrew: "תְּפִלַּת עַרְבִית",
    section: "maariv-weeknights",
  },
};

const SERVICE_SOURCES = Object.freeze({
  shabbat: "kehot:shabbat-siddur-idml:sha256:92a6d4a246c3d7ea4eae70d3067413e9ccd5b37ec84af0d9a8359e199e4fc67c",
  shabbatRoshChodesh: "https://www.chabad.org/library/article_cdo/aid/4720586/jewish/Order-of-Prayers-for-Shabbat-Rosh-Chodesh.htm",
  pesach: "https://www.chabad.org/holidays/passover/pesach_cdo/aid/5475828/jewish/Prayers-for-When-Last-Days-of-Pesach-Are-Fri-Shabbat.htm",
  shavuot: "https://www.chabad.org/library/article_cdo/aid/4685331/jewish/Shulchan-Aruch-Chapter-494-The-Order-of-Prayers-on-the-Festival-of-Shavuos.htm",
  sukkot: "https://www.chabad.org/library/article_cdo/aid/6589779/jewish/Sukkot-Prayer.htm",
  sheminiAtzeret: "https://www.chabad.org/library/article_cdo/aid/4903813/jewish/Order-of-Prayers-for-Shemini-Atzeret-and-Simchat-Torah-in-Quarantine.htm",
  roshHashanah: "https://www.chabad.org/library/article_cdo/aid/4879158/jewish/Order-of-Prayers-for-Rosh-Hashanah-In-Lockdown.htm",
  yomKippur: "https://www.chabad.org/library/article_cdo/aid/4893551/jewish/Order-of-Yom-Kippur-Prayers-In-Lockdown.htm",
  yizkor: "https://www.chabad.org/library/article_cdo/aid/371509/jewish/Yizkor-The-Memorial-Prayer.htm",
});

const CORE_CITY_NAMES = [
  "New York", "Brooklyn", "Los Angeles", "Miami", "Chicago", "Boston", "Toronto", "Montreal",
  "London", "Paris", "Johannesburg", "Melbourne", "Sydney", "Jerusalem", "Tel Aviv", "Tzfat",
];

// Hebcal's compact built-in city list does not include most Florida population
// centers. These local entries improve the human-readable label and timezone
// fallback while all zmanim continue to use the device's exact coordinates.
const CUSTOM_LOCATIONS = [
  { name: "Boca Raton", latitude: 26.3683, longitude: -80.1289, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Brooklyn", latitude: 40.6782, longitude: -73.9442, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Fort Lauderdale", latitude: 26.1224, longitude: -80.1373, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Fort Myers", latitude: 26.6406, longitude: -81.8723, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Gainesville", latitude: 29.6516, longitude: -82.3248, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Jacksonville", latitude: 30.3322, longitude: -81.6557, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Key West", latitude: 24.5551, longitude: -81.7800, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Naples", latitude: 26.1423, longitude: -81.7948, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Orlando", latitude: 28.5383, longitude: -81.3792, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Panama City, Florida", latitude: 30.1588, longitude: -85.6602, tzid: "America/Chicago", il: false, cc: "US" },
  { name: "Pensacola", latitude: 30.4213, longitude: -87.2169, tzid: "America/Chicago", il: false, cc: "US" },
  { name: "Tallahassee", latitude: 30.4383, longitude: -84.2807, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Tampa", latitude: 27.9506, longitude: -82.4572, tzid: "America/New_York", il: false, cc: "US" },
  { name: "West Palm Beach", latitude: 26.7153, longitude: -80.0534, tzid: "America/New_York", il: false, cc: "US" },
  { name: "Tzfat", latitude: 32.9646, longitude: 35.4960, tzid: "Asia/Jerusalem", il: true, cc: "IL" },
];

const CITY_NAMES = [...new Set([...CORE_CITY_NAMES, ...CUSTOM_LOCATIONS.map((entry) => entry.name)])];

function customLocation(name) {
  const entry = CUSTOM_LOCATIONS.find((item) => item.name.toLowerCase() === String(name || "").toLowerCase());
  return entry ? new Location(entry.latitude, entry.longitude, entry.il, entry.tzid, entry.name, entry.cc) : undefined;
}

function namedLocation(name) {
  return customLocation(name) || Location.lookup(name);
}

function distanceMeters(latitudeA, longitudeA, latitudeB, longitudeB) {
  const radians = (value) => value * Math.PI / 180;
  const deltaLatitude = radians(latitudeB - latitudeA);
  const deltaLongitude = radians(longitudeB - longitudeA);
  const a = Math.sin(deltaLatitude / 2) ** 2
    + Math.cos(radians(latitudeA)) * Math.cos(radians(latitudeB)) * Math.sin(deltaLongitude / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function locationCandidates() {
  return CITY_NAMES.map((name) => namedLocation(name)).filter(Boolean);
}

export function localizeCoordinates(latitude, longitude, fallbackTzid = "UTC") {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) throw new RangeError("Valid coordinates are required");
  const nearest = locationCandidates().map((location) => ({
    location,
    distance: distanceMeters(latitude, longitude, location.getLatitude(), location.getLongitude()),
  })).sort((a, b) => a.distance - b.distance)[0];
  const nearby = nearest?.distance <= 300000 ? nearest : null;
  const il = latitude >= 29.3 && latitude <= 33.5 && longitude >= 34.1 && longitude <= 35.9;
  let tzid = fallbackTzid || "UTC";
  try {
    // Resolve the civil timezone from the exact coordinates. A nearest-city
    // timezone is not reliable near boundaries (including Florida's
    // Eastern/Central boundary) or when the device travels internationally.
    tzid = tzLookup(latitude, longitude);
  } catch (_) {}
  return {
    name: nearby ? `Near ${nearby.location.getName()}` : "Current location",
    nearestCity: nearby?.location.getName() || "",
    distanceMeters: nearby?.distance ?? null,
    latitude,
    longitude,
    tzid,
    il,
  };
}

export function resolveLocation(config = {}) {
  if (Number.isFinite(config.latitude) && Number.isFinite(config.longitude)) {
    const localized = localizeCoordinates(config.latitude, config.longitude, config.tzid || "UTC");
    const label = config.label && config.label !== "Current location" ? config.label : localized.name;
    const il = typeof config.il === "boolean" ? config.il : localized.il;
    return new Location(config.latitude, config.longitude, il, config.tzid || localized.tzid, label, il ? "IL" : undefined);
  }
  return namedLocation(config.city || "New York") || Location.lookup("New York");
}

function validHebrewBirthday(day, month, year) {
  const first = new HDate(1, month, year);
  return new HDate(Math.min(Math.max(1, Number(day) || 1), first.daysInMonth()), month, year);
}

export function personalPsalmForBirth(birth, instant = new Date(), config = {}) {
  const type = birth?.type === "hebrew" ? "hebrew" : "gregorian";
  const location = resolveLocation(config);
  const timeZone = location.getTzid();
  const todayParts = locationDateParts(instant, timeZone);
  const todayGregorian = new Date(todayParts.year, todayParts.month - 1, todayParts.day, 12);
  const currentHebrew = new HDate(todayGregorian);
  let age;
  let nextBirthday;

  if (type === "hebrew") {
    const year = Number(birth.year);
    const month = Number(birth.month);
    const day = Number(birth.day);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) || year < 1 || month < 1 || month > 13 || day < 1 || day > 30) {
      throw new RangeError("Enter a valid Hebrew birth date");
    }
    const thisBirthday = validHebrewBirthday(day, month, currentHebrew.getFullYear());
    const birthdayPassed = currentHebrew.abs() >= thisBirthday.abs();
    age = currentHebrew.getFullYear() - year - (birthdayPassed ? 0 : 1);
    nextBirthday = (birthdayPassed ? validHebrewBirthday(day, month, currentHebrew.getFullYear() + 1) : thisBirthday).render("en");
  } else {
    const match = String(birth?.date || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) throw new RangeError("Enter a valid civil birth date");
    const [, yearText, monthText, dayText] = match;
    const year = Number(yearText); const month = Number(monthText); const day = Number(dayText);
    const birthDate = new Date(year, month - 1, day, 12);
    if (birthDate.getFullYear() !== year || birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) throw new RangeError("Enter a valid civil birth date");
    const birthdayPassed = todayParts.month > month || (todayParts.month === month && todayParts.day >= day);
    age = todayParts.year - year - (birthdayPassed ? 0 : 1);
    const nextYear = birthdayPassed ? todayParts.year + 1 : todayParts.year;
    nextBirthday = new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(new Date(nextYear, month - 1, day, 12));
  }
  if (!Number.isInteger(age) || age < 0) throw new RangeError("Birth date cannot be in the future");
  return { age, chapter: Math.min(150, age + 1), nextBirthday, capped: age + 1 > 150 };
}

function locationDateParts(instant, timeZone) {
  return Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "numeric", day: "numeric", timeZone })
      .formatToParts(instant)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  );
}

function formatTime(date, timeZone, locale = "en-US") {
  return date instanceof Date && !Number.isNaN(date.valueOf())
    ? new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone }).format(date)
    : "—";
}

function item(section, title, hebrew, reason, type = "addition", heading = "") {
  return { section, title, hebrew, reason, type, heading };
}

function addUnique(items, value) {
  if (!items.some((entry) => entry.section === value.section && entry.heading === value.heading)) items.push(value);
}

function orderAdditionsByServicePlan(additions, planSections) {
  const planOrder = new Map(planSections.map((entry, index) => [entry.id, index]));
  const orderedLiturgicalItems = additions
    .map((entry, index) => ({ entry, index, order: planOrder.get(entry.section) }))
    .filter(({ order }) => Number.isInteger(order))
    .sort((left, right) => left.order - right.order || left.index - right.index)
    .map(({ entry }) => entry);
  let cursor = 0;
  // Preserve the established positions of contextual items that are not part
  // of the service (candle lighting, Havdalah, bedtime Shema, etc.), while the
  // prayer additions themselves follow the canonical rendered service.
  return additions.map((entry) => (
    planOrder.has(entry.section) ? orderedLiturgicalItems[cursor++] : entry
  ));
}

function eventSummary(event, timeZone) {
  return {
    name: event.render("en"),
    hebrew: event.render("he"),
    description: event.getDesc(),
    basename: event.basename(),
    flags: event.getFlags(),
    emoji: event.getEmoji(),
    time: event.eventTime instanceof Date ? formatTime(event.eventTime, timeZone) : "",
    timestamp: event.eventTime instanceof Date ? event.eventTime.toISOString() : "",
  };
}

function isSacredDay(events) {
  return events.some((event) => {
    const mask = event.getFlags();
    return Boolean(mask & flags.CHAG) && !Boolean(mask & flags.CHOL_HAMOED);
  });
}

function isTachanunSaidOnDate(hdate) {
  const month = hdate.getMonth();
  const day = hdate.getDate();
  if (month === months.NISAN) return false;
  if (month === months.SIVAN && day <= 12) return false;
  if (month === months.TAMUZ && [12, 13].includes(day)) return false;
  if (month === months.AV && [9, 15].includes(day)) return false;
  if (month === months.ELUL && day === 29) return false;
  if (month === months.TISHREI && (day <= 2 || day >= 9)) return false;
  if (month === months.KISLEV && [10, 19, 20].includes(day)) return false;
  if (month === months.KISLEV && day >= 25) return false;
  if (month === months.SHVAT && day === 15) return false;
  if (month === months.IYYAR && [14, 18].includes(day)) return false;
  if ((month === months.ADAR_I || month === months.ADAR_II) && [14, 15].includes(day)) return false;
  return true;
}

function isTachanunSaid(hdate, period, weekday, {
  shabbat, sacredDay, isRoshChodesh, isChanukah, isPurim, isTishaBAv, isCholHamoed,
}) {
  if (shabbat || sacredDay || isRoshChodesh || isChanukah || isPurim || isTishaBAv || isCholHamoed) return false;
  if (!isTachanunSaidOnDate(hdate)) return false;
  if (period.id !== "afternoon") return true;

  // Chabad omits Tachnun at Minchah on Friday and on the afternoon before a
  // date on which Tachnun is omitted. Rosh Chodesh can begin after either the
  // 29th or 30th day of the preceding month, so inspect the actual next date.
  if (weekday === 5) return false;
  const tomorrow = new HDate(hdate.abs() + 1);
  if ([1, 30].includes(tomorrow.getDate())) return false;
  if ((hdate.getMonth() === months.ELUL && hdate.getDate() === 28)
    || (hdate.getMonth() === months.IYYAR && hdate.getDate() === 13)) return true;
  return isTachanunSaidOnDate(tomorrow);
}

function tachnunDateOmissionReason(hdate, {
  shabbat, sacredDay, holidayName, isRoshChodesh, isChanukah, isPurim, isTishaBAv, isCholHamoed,
}) {
  if (shabbat) return "Shabbat";
  if (isRoshChodesh) return "Rosh Chodesh";
  if (isChanukah) return "Chanukah";
  if (isPurim) return "Purim";
  if (isTishaBAv) return "Tisha B’Av";
  if (isCholHamoed) return "Chol Hamoed";
  if (sacredDay) return holidayName || "Yom Tov";

  const month = hdate.getMonth();
  const day = hdate.getDate();
  if (month === months.NISAN) return "The month of Nisan";
  if (month === months.SIVAN && day <= 12) return "Rosh Chodesh Sivan through 12 Sivan";
  if (month === months.TAMUZ && [12, 13].includes(day)) return "Chag HaGeulah · 12–13 Tammuz";
  if (month === months.AV && day === 9) return "Tisha B’Av";
  if (month === months.AV && day === 15) return "15 Av";
  if (month === months.ELUL && day === 29) return "Erev Rosh Hashanah";
  if (month === months.TISHREI && day <= 2) return "Rosh Hashanah";
  if (month === months.TISHREI && day === 9) return "Erev Yom Kippur";
  if (month === months.TISHREI && day === 10) return "Yom Kippur";
  if (month === months.TISHREI && day >= 11) return "The post–Yom Kippur days of Tishrei";
  if (month === months.KISLEV && day === 10) return "10 Kislev · Chag HaGeulah";
  if (month === months.KISLEV && [19, 20].includes(day)) return "19–20 Kislev · Chag HaGeulah";
  if (month === months.KISLEV && day >= 25) return "Chanukah";
  if (month === months.SHVAT && day === 15) return "15 Shevat";
  if (month === months.IYYAR && day === 14) return "Pesach Sheni";
  if (month === months.IYYAR && day === 18) return "Lag BaOmer";
  if (month === months.ADAR_I && [14, 15].includes(day)) return "Purim Katan";
  if (month === months.ADAR_II && [14, 15].includes(day)) return "Purim";
  return "Chabad custom for this date";
}

function tachnunOmissionReason(hdate, period, weekday, context) {
  const currentDateOmitsTachnun = context.shabbat || context.sacredDay || context.isRoshChodesh
    || context.isChanukah || context.isPurim || context.isTishaBAv || context.isCholHamoed
    || !isTachanunSaidOnDate(hdate);
  if (currentDateOmitsTachnun) return tachnunDateOmissionReason(hdate, context);
  if (period.id === "afternoon" && weekday === 5) return "Erev Shabbat";
  if (period.id === "afternoon") {
    const tomorrow = new HDate(hdate.abs() + 1);
    if ([1, 30].includes(tomorrow.getDate())) return "Erev Rosh Chodesh";
    const tomorrowReason = tachnunDateOmissionReason(tomorrow, {});
    return tomorrowReason === "Chabad custom for this date"
      ? "The eve of a date when Tachnun is omitted"
      : `Eve of ${tomorrowReason}`;
  }
  return "Chabad custom for this date";
}

function seasonalRainRule(hdate, period) {
  const month = hdate.getMonth();
  const day = hdate.getDate();
  if (month === months.TISHREI && day === 22) return period.id !== "morning";
  if (month === months.NISAN && day === 15) return period.id === "morning";
  return month === months.TISHREI ? day > 22
    : [months.CHESHVAN, months.KISLEV, months.TEVET, months.SHVAT, months.ADAR_I, months.ADAR_II].includes(month)
      || (month === months.NISAN && day < 15);
}

function isGregorianLeapYear(year) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function talUmatarRule({ hdate, il, dateParts, period }) {
  if (hdate.getMonth() === months.NISAN && hdate.getDate() >= 15) return false;
  if (il) {
    const month = hdate.getMonth();
    return month === months.CHESHVAN ? hdate.getDate() >= 7
      : [months.KISLEV, months.TEVET, months.SHVAT, months.ADAR_I, months.ADAR_II].includes(month)
        || (month === months.NISAN && hdate.getDate() < 15);
  }
  if ([months.TEVET, months.SHVAT, months.ADAR_I, months.ADAR_II].includes(hdate.getMonth())
    || (hdate.getMonth() === months.NISAN && hdate.getDate() < 15)) return true;
  const startDay = isGregorianLeapYear(dateParts.year + 1) ? 5 : 4;
  if (dateParts.month !== 12) return false;
  if (dateParts.day > startDay) return true;
  return dateParts.day === startDay && period.id === "evening";
}

function buildDailyPrayerPlan({
  hdate, period, weekday, il, shabbat, sacredDay, holidayEvents, timedEvents, additions, dateParts,
}) {
  const observedEvents = holidayEvents.filter((event) => !/^Erev /.test(event.getDesc()));
  const names = observedEvents.map((event) => event.getDesc());
  const basenames = observedEvents.map((event) => event.basename());
  const masks = observedEvents.reduce((mask, event) => mask | event.getFlags(), 0);
  const isFast = Boolean(masks & (flags.MINOR_FAST | flags.MAJOR_FAST));
  const isPublicFast = isFast && !names.some((name) => /Ta'anit Bechorot|Yom Kippur Katan/i.test(name));
  const isTishaBAv = names.some((name) => /Tish.?a B.?Av/i.test(name));
  const isRoshChodesh = Boolean(masks & flags.ROSH_CHODESH);
  const isCholHamoed = Boolean(masks & flags.CHOL_HAMOED);
  const chanukahDay = hdate.abs() - new HDate(25, months.KISLEV, hdate.getFullYear()).abs() + 1;
  const isChanukah = basenames.includes("Chanukah") && chanukahDay >= 1 && chanukahDay <= 8;
  const isPurim = basenames.includes("Purim") && !names.some((name) => name.includes("Katan"));
  const isPesach = basenames.includes("Pesach");
  // Sukkot and Shemini Atzeret use different insertions in after-blessings.
  // Some calendar event basenames retain the Sukkot family name around the
  // boundary, so keep these day ranges explicit and observance-aware.
  const isSukkot = hdate.getMonth() === months.TISHREI && hdate.getDate() >= 15 && hdate.getDate() <= 21;
  const isShavuot = hdate.getMonth() === months.SIVAN
    && (hdate.getDate() === 6 || (!il && hdate.getDate() === 7));
  const isSheminiAtzeretDay = hdate.getMonth() === months.TISHREI && hdate.getDate() === 22;
  // In the Diaspora, Simchat Torah is the second day of Shemini Atzeret and
  // therefore retains the Shemini Atzeret insertion in after-blessings even
  // though its service order is a distinct Simchat Torah variant.
  const isSheminiAtzeret = isSheminiAtzeretDay
    || (!il && hdate.getMonth() === months.TISHREI && hdate.getDate() === 23);
  const isSimchatTorah = hdate.getMonth() === months.TISHREI
    && (il ? hdate.getDate() === 22 : hdate.getDate() === 23);
  const isRoshHashanah = hdate.getMonth() === months.TISHREI && hdate.getDate() <= 2;
  const isYomKippur = hdate.getMonth() === months.TISHREI && hdate.getDate() === 10;
  const afterYomKippur = hdate.getMonth() === months.TISHREI && hdate.getDate() === 11;
  const erevYomKippur = hdate.getMonth() === months.TISHREI && hdate.getDate() === 9;
  const tenDays = hdate.getMonth() === months.TISHREI && hdate.getDate() <= 10;
  const tachnunContext = {
    shabbat, sacredDay, isRoshChodesh, isChanukah, isPurim, isTishaBAv, isCholHamoed,
    holidayName: names[0] || "",
  };
  const tachnun = isTachanunSaid(hdate, period, weekday, tachnunContext);
  const torahReading = shabbat || sacredDay || [1, 4].includes(weekday) || isRoshChodesh || isChanukah || isPurim || isPublicFast || isCholHamoed;
  const leyningDate = shabbat && period.id === "afternoon" ? new HDate(hdate.abs() + 2) : hdate;
  const leyning = torahReading ? getLeyningOnDate(leyningDate, il) : undefined;
  const leyningAliyot = shabbat && period.id === "morning"
    ? leyning?.fullkriyah
    : leyning?.weekday || leyning?.fullkriyah;
  let dailyTorah = leyningAliyot ? {
    name: leyning.name.en,
    hebrew: leyning.name.he,
    type: leyning.type,
    summary: makeLeyningSummary(leyningAliyot),
    ...(leyning.haftara ? { haftara: leyning.haftara, haft: leyning.haft } : {}),
    aliyot: Object.entries(leyningAliyot)
      .filter(([key]) => /^\d+$/.test(key))
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([number, aliyah]) => ({
        number: Number(number),
        book: aliyah.k,
        start: aliyah.b,
        end: aliyah.e,
      })),
  } : null;
  // Hebcal's generic holiday label obscures that this is the distinct
  // Hoshana Rabbah reading. Keep its authoritative aliyah ranges untouched,
  // but use the same specific day name throughout the reader and shortcuts.
  if (dailyTorah && isSukkot && hdate.getDate() === 21) {
    dailyTorah.name = "Hoshana Rabbah";
  }
  // Hebcal's Tishah B'Av record describes the morning reading, and the minor
  // fast records do not carry the Minchah Haftarah. Every public-fast Minchah
  // instead uses Vayechal followed by Dirshu (Isaiah 55:6-56:8).
  if (isPublicFast && period.id === "afternoon") {
    dailyTorah = {
      name: names.find((name) => /Av|Fast|Tevet|Tammuz|Gedaliah|Esther/i.test(name)) || "Public Fast",
      hebrew: isTishaBAv ? "תִּשְׁעָה בְּאָב" : "תַּעֲנִית צִבּוּר",
      type: "public-fast-minchah",
      summary: "Exodus 32:11-14, 34:1-10",
      haftara: "Isaiah 55:6-56:8",
      haft: { k: "Isaiah", b: "55:6", e: "56:8" },
      aliyot: [
        { number: 1, book: "Exodus", start: "32:11", end: "32:14" },
        { number: 2, book: "Exodus", start: "34:1", end: "34:3" },
        { number: 3, book: "Exodus", start: "34:4", end: "34:10" },
      ],
    };
  }
  if (isYomKippur && period.id === "afternoon") {
    dailyTorah = {
      name: "Yom Kippur Minchah",
      hebrew: "מִנְחָה לְיוֹם הַכִּפּוּרִים",
      type: "yom-kippur-minchah",
      summary: "Leviticus 18:1-30",
      haftara: "Jonah 1:1-4:11; Micah 7:18-20",
      haft: { k: "Jonah", b: "1:1", e: "4:11", additional: [{ k: "Micah", b: "7:18", e: "7:20" }] },
      aliyot: [
        { number: 1, book: "Leviticus", start: "18:1", end: "18:5" },
        { number: 2, book: "Leviticus", start: "18:6", end: "18:21" },
        { number: 3, book: "Leviticus", start: "18:22", end: "18:30" },
      ],
    };
  }
  const pesachFullHallel = isPesach && hdate.getMonth() === months.NISAN
    && (hdate.getDate() === 15 || (!il && hdate.getDate() === 16));
  const fullHallel = isChanukah || pesachFullHallel || isShavuot || isSukkot || isSheminiAtzeret || isSimchatTorah;
  const halfHallel = isRoshChodesh || (isPesach && !pesachFullHallel);
  const hallel = !isRoshHashanah && !isYomKippur && (fullHallel || halfHallel);
  const havdalah = timedEvents.some((event) => event.getDesc() === "Havdalah");
  const cholHamoedDay = isSukkot
    ? Math.max(1, hdate.getDate() - 15 - (il ? 0 : 1))
    : isPesach
      ? Math.max(1, hdate.getDate() - 15 - (il ? 0 : 1))
      : 0;
  // Hoshanot follow the ordinal day of Sukkot itself, not the ordinal day of
  // Chol Hamoed.  In the Diaspora, 16 Tishrei is the second festival day but
  // still uses the second Hoshana; 17 Tishrei therefore uses the third.  The
  // installed Kehot Siddur applies exactly that date-based remapping.
  const sukkotDay = isSukkot ? hdate.getDate() - 14 : 0;
  // When Shabbat suppresses Hoshanot, Kehot carries that day's Hoshana into
  // the following (Sunday) service before the Hoshana assigned to that day.
  // Hoshana Rabbah already repeats the complete sequence and needs no extra
  // carry-forward branch.
  const hoshanotDays = !isSukkot || shabbat ? []
    : sukkotDay === 7 ? [1, 2, 3, 4, 5, 6, 7]
      : weekday === 0 && sukkotDay >= 2 && sukkotDay <= 6
        ? [sukkotDay - 1, sukkotDay]
        : [sukkotDay];
  const rules = {
    hdateMonth: hdate.getMonth(),
    hdateDay: hdate.getDate(),
    weekday,
    il,
    shabbat,
    sacredDay,
    tachnun,
    tachnunReason: tachnun ? "" : tachnunOmissionReason(hdate, period, weekday, tachnunContext),
    torahReading,
    dailyTorah,
    tenDays,
    isFast,
    isPublicFast,
    isTishaBAv,
    isRoshChodesh,
    isCholHamoed,
    isChanukah,
    isPurim,
    isPesach,
    isSukkot,
    isShavuot,
    isSheminiAtzeret,
    isSimchatTorah,
    isRoshHashanah,
    isYomKippur,
    afterYomKippur,
    erevYomKippur,
    hallel,
    fullHallel,
    halfHallel,
    omer: additions.some((entry) => entry.section === "counting-the-omer"),
    havdalah,
    moridHageshem: seasonalRainRule(hdate, period),
    talUmatar: talUmatarRule({ hdate, il, dateParts, period }),
    omitMizmorLetodah: (isCholHamoed && isPesach)
      || (hdate.getMonth() === months.NISAN && hdate.getDate() === 14)
      || (hdate.getMonth() === months.TISHREI && hdate.getDate() === 9),
    ledovid: ["morning", "afternoon"].includes(period.id) && (
      (hdate.getMonth() === months.AV && hdate.getDate() === 30 && period.id === "afternoon")
      || hdate.getMonth() === months.ELUL
      || (hdate.getMonth() === months.TISHREI && hdate.getDate() <= 21)
    ),
    chanukahDay: isChanukah ? chanukahDay : 0,
    cholHamoedDay,
    sukkotDay,
    hoshanotDays,
  };

  const sections = [];
  const sourceReferences = new Set();
  if (shabbat) sourceReferences.add(SERVICE_SOURCES.shabbat);
  const add = (id, reason = "", presentation = null, sourceReference = "") => {
    if (id && !sections.some((entry) => entry.id === id)) {
      sections.push({ id, semanticId: id, reason, ...(presentation || {}), ...(sourceReference ? { sourceReference } : {}) });
      if (sourceReference) sourceReferences.add(sourceReference);
    }
  };

  const addMorningFoundation = () => {
    add("morning-blessings");
    add("order-of-putting-on-the-tzitzit");
    add("order-of-putting-on-the-tallit");
    add("morning-prayer");
    add("kaddish-d-rabbanan", "After Korbanot");
    add("hodu");
    add((shabbat || sacredDay) ? "shabbat-pesukei-dezimra" : "verses-of-praise");
    if (shabbat || sacredDay) add("shabbat-blessings-of-shema");
    else {
      add("barchu");
      add("blessings-of-the-shema");
      add("the-shema");
    }
  };
  const addCalculatedTorah = (reason = "Festival Torah reading and Haftarah") => {
    add(shabbat ? "shabbat-torah-service-opening" : "weekday-torah-service-opening");
    add(
      "torah-reading-weekly-portions",
      dailyTorah ? `${dailyTorah.name} · ${dailyTorah.summary}${dailyTorah.haftara ? ` · Haftarah ${dailyTorah.haftara}` : ""}` : reason,
      dailyTorah ? { title: `Torah Reading — ${dailyTorah.name}`, hebrew: dailyTorah.hebrew || "" } : null,
    );
    if (shabbat) add("shabbat-torah-service-conclusion");
    else {
      // Festival and High-Holiday readings normally carry a Haftarah. Their
      // weekday post-reading material is composed inside the calculated
      // reading so the concluding aliyah blessing, Hagbah, and Haftarah stay
      // in their actual order. Retain the standalone continuation as a safe
      // source-faithful fallback when a date has no Haftarah assignment.
      if (!dailyTorah?.haftara) add("weekday-torah-service-conclusion");
      add("returning-the-torah");
    }
  };
  const yizkorDay = isYomKippur || isSheminiAtzeretDay
    || (isPesach && hdate.getDate() === (il ? 21 : 22))
    || (isShavuot && hdate.getDate() === (il ? 6 : 7));
  const nassiDay = hdate.getMonth() === months.NISAN && hdate.getDate() >= 1 && hdate.getDate() <= 13;
  rules.nassi = nassiDay;

  if (sacredDay) {
    if (isRoshHashanah) {
      sourceReferences.add(SERVICE_SOURCES.roshHashanah);
      if (period.id === "evening") {
        if (shabbat) add("kabbalat-shabbat", "Friday evening");
        add("festival-maariv-opening");
        add("rosh-hashanah-amidah", "Rosh Hashanah Maariv Amidah and Avinu Malkeinu", null, SERVICE_SOURCES.roshHashanah);
        add("festival-maariv-conclusion");
      } else if (period.id === "morning") {
        addMorningFoundation();
        add("rosh-hashanah-amidah", "Rosh Hashanah Shacharit Amidah and Avinu Malkeinu", null, SERVICE_SOURCES.roshHashanah);
        add(shabbat ? "shabbat-song-of-the-day" : "song-of-the-day");
        if (rules.ledovid) add("ledovid-hashem-ori", "Through Hoshana Rabbah");
        addCalculatedTorah();
        if (!shabbat) add("rosh-hashanah-shofar", "The shofar is not sounded on Shabbat", null, SERVICE_SOURCES.roshHashanah);
        add("rosh-hashanah-musaf", "Silent Musaf and labeled highlights of the chazzan’s repetition", null, SERVICE_SOURCES.roshHashanah);
        add("ein-kelokeinu");
        add("kaddish-d-rabbanan-167", "After Ein Kelokeinu");
        add("rosh-hashanah-aleinu", "Aleinu and Al Tira after Musaf", null, SERVICE_SOURCES.roshHashanah);
        add("six-remembrances");
        add("daily-tehillim");
        add("hayom-yom");
      } else {
        add("festival-minchah-opening");
        if (shabbat) addCalculatedTorah();
        add("rosh-hashanah-amidah", "Rosh Hashanah Minchah Amidah and Avinu Malkeinu", null, SERVICE_SOURCES.roshHashanah);
        add("festival-minchah-conclusion");
        const firstRoshHashanahWasShabbat = new HDate(1, months.TISHREI, hdate.getFullYear()).greg().getDay() === 6;
        if ((hdate.getDate() === 1 && !shabbat) || (hdate.getDate() === 2 && firstRoshHashanahWasShabbat)) {
          add("rosh-hashanah-tashlich", "After Minchah on the first day, or the second day when the first is Shabbat", null, SERVICE_SOURCES.roshHashanah);
        }
      }
    } else if (isYomKippur) {
      sourceReferences.add(SERVICE_SOURCES.yomKippur);
      if (period.id === "evening") {
        add("yom-kippur-kol-nidrei", "Before Maariv", null, SERVICE_SOURCES.yomKippur);
        add("festival-maariv-opening");
        add("yom-kippur-amidah", "Maariv Amidah, Selichot, and Avinu Malkeinu", null, SERVICE_SOURCES.yomKippur);
        if (rules.ledovid) add("ledovid-hashem-ori", "After Avinu Malkeinu", null, SERVICE_SOURCES.yomKippur);
        add("yom-kippur-aleinu", "Maariv conclusion", null, SERVICE_SOURCES.yomKippur);
        add("daily-tehillim", "Yom Kippur Tehillim sequence");
        add("prayer-before-retiring-at-night");
      } else if (period.id === "morning") {
        addMorningFoundation();
        add("yom-kippur-amidah", "Shacharit Amidah, Selichot, and Avinu Malkeinu", null, SERVICE_SOURCES.yomKippur);
        add(shabbat ? "shabbat-song-of-the-day" : "song-of-the-day");
        if (rules.ledovid) add("ledovid-hashem-ori", "Psalm 27 after Hoshe’anu", null, SERVICE_SOURCES.yomKippur);
        addCalculatedTorah();
        add("festival-yizkor", "After the Torah reading and before Musaf", null, SERVICE_SOURCES.yizkor);
        add("yom-kippur-musaf", "Silent Musaf and labeled highlights of the chazzan’s repetition", null, SERVICE_SOURCES.yomKippur);
        add("daily-tehillim");
        add("six-remembrances");
      } else {
        add("festival-minchah-opening");
        addCalculatedTorah("Yom Kippur Minchah Torah reading and Haftarah");
        add("yom-kippur-amidah", "Minchah Amidah, Selichot, and Avinu Malkeinu", null, SERVICE_SOURCES.yomKippur);
        add("yom-kippur-minchah-transition", "Complete Kaddish and Psalm 27 before Neilah", null, SERVICE_SOURCES.yomKippur);
        add("yom-kippur-neilah", "Neilah Amidah, labeled chazzan material, and conclusion", null, SERVICE_SOURCES.yomKippur);
        add("yom-kippur-aleinu", "Conclusion of Neilah", null, SERVICE_SOURCES.yomKippur);
        add("daily-tehillim", "Completion of the Yom Kippur Tehillim sequence");
      }
    } else {
      const festivalSource = isPesach ? SERVICE_SOURCES.pesach
        : isShavuot ? SERVICE_SOURCES.shavuot
          : isSukkot ? SERVICE_SOURCES.sukkot : SERVICE_SOURCES.sheminiAtzeret;
      sourceReferences.add(festivalSource);
      if (period.id === "evening") {
        if (shabbat) add("kabbalat-shabbat", "Friday evening");
        add("festival-maariv-opening");
        add("festival-amidah", "Yom Tov Maariv Amidah", null, festivalSource);
        if (isSheminiAtzeret || isSimchatTorah || rules.omer) {
          add("festival-maariv-conclusion-through-kaddish");
          if (isSheminiAtzeret || isSimchatTorah) add("simchat-torah-hakafot", "Hakafot before Aleinu", null, SERVICE_SOURCES.sheminiAtzeret);
          if (rules.omer) add("counting-the-omer", "Before Aleinu");
          add("festival-maariv-conclusion-aleinu");
        } else add("festival-maariv-conclusion");
      } else if (period.id === "morning") {
        addMorningFoundation();
        add("festival-amidah", "Yom Tov Shacharit Amidah", null, festivalSource);
        if (isSukkot && !shabbat) {
          add("blessings-over-the-lulav", "Before Hallel");
        }
        if (hallel) add("hallel", fullHallel ? "Complete Hallel" : "Half Hallel");
        if (isSukkot && !shabbat) {
          add("hoshanot", sukkotDay === 7
            ? "Hoshanot for Hoshana Rabbah"
            : `Hoshanot for ${hoshanotDays.length > 1 ? `days ${hoshanotDays.join(" and ")}` : `day ${sukkotDay}`} of Sukkot`);
          add("post-hoshanot-complete-kaddish", "Complete Kaddish after Hoshanot");
        }
        if (isSimchatTorah) add("simchat-torah-hakafot", "Day Hakafot before the Torah reading", null, SERVICE_SOURCES.sheminiAtzeret);
        add(shabbat ? "shabbat-song-of-the-day" : "song-of-the-day");
        if (rules.ledovid) add("ledovid-hashem-ori", "Through Hoshana Rabbah");
        addCalculatedTorah();
        if (yizkorDay) add("festival-yizkor", "After the Torah reading and before Musaf", null, SERVICE_SOURCES.yizkor);
        add("festival-musaf-amidah", isSheminiAtzeretDay ? "Includes the Prayer for Rain and begins Mashiv haruach" : isPesach && hdate.getDate() === 15 ? "Includes the Prayer for Dew" : "Festival Musaf", null, festivalSource);
        add("ein-kelokeinu");
        add("kaddish-d-rabbanan-167", "After Ein Kelokeinu");
        add("aleinu");
        add("daily-tehillim");
        add("six-remembrances");
        if (nassiDay) add("nassi", `Nassi for ${hdate.getDate()} Nisan`);
        add("hayom-yom");
      } else {
        add("festival-minchah-opening");
        if (shabbat) addCalculatedTorah();
        add("festival-amidah", "Yom Tov Minchah Amidah", null, festivalSource);
        add("festival-minchah-conclusion");
      }
    }
  }

  if (!shabbat && !sacredDay) {
    if (period.id === "morning") {
      const morningSelichot = selichotServiceForDate(hdate);
      if (morningSelichot) {
        add(
          morningSelichot.section,
          "Before Shacharit",
          { title: morningSelichot.title, hebrew: morningSelichot.hebrew },
        );
      }
      add("morning-blessings");
      if (!isTishaBAv) {
        add("order-of-putting-on-the-tzitzit");
        add("order-of-putting-on-the-tallit");
        if (!isCholHamoed) add("order-of-putting-on-the-tefillin");
      }
      add("morning-prayer");
      add("kaddish-d-rabbanan", "After Korbanot");
      add("hodu");
      add("verses-of-praise");
      add("barchu");
      add("blessings-of-the-shema");
      add("the-shema");
      add("the-amidah");
      if (isTishaBAv) {
        add("shacharit-half-kaddish", "After the Amidah");
        add("tisha-bav-morning", "Dedicated Torah reading, Haftarah, Kinot, and conclusion");
        add("shacharit-conclusion", "Continue after Kinot; Tishah B’Av omissions are applied");
        add("shacharit-complete-kaddish", "Titkabel is omitted on Tishah B’Av morning");
        add("aleinu");
        add("mourner-s-kaddish", "After Aleinu");
        add("hayom-yom", "Today’s daily Hayom Yom study");
      } else {
        if (rules.tachnun) add("viduy-and-tachnun");
        if (isPublicFast) {
          const fastSection = names.some((name) => /Tevet/i.test(name)) ? "for-the-tenth-of-tevet"
            : names.some((name) => /Esther/i.test(name)) ? "for-the-fast-of-esther"
              : names.some((name) => /Tammuz/i.test(name)) ? "for-the-seventeenth-of-tammuz"
                : names.some((name) => /Gedaliah/i.test(name)) ? "selichot-tzom-gedaliah" : "";
          add(fastSection, "Selichot for today’s fast");
          add("the-long-avinu-malkeinu", "Public fast");
        }
        if (tenDays && hdate.getDate() <= 8) {
          add("the-long-avinu-malkeinu", "Ten Days of Repentance");
        }
        add("shacharit-half-kaddish", rules.tachnun ? "After Tachnun" : "After the Amidah");
        if (isCholHamoed && isSukkot && !shabbat) {
          add("blessings-over-the-lulav", "Before Hallel on Chol Hamoed Sukkot");
        }
        if (hallel) add("hallel", fullHallel ? "Complete Hallel" : "Half Hallel");
        if (isCholHamoed && isSukkot && !shabbat) {
          add("hoshanot", sukkotDay === 7
            ? "Hoshanot for Hoshana Rabbah"
            : `Hoshanot for ${hoshanotDays.length > 1 ? `days ${hoshanotDays.join(" and ")}` : `day ${sukkotDay}`} of Sukkot`);
          add("post-hoshanot-complete-kaddish", "Complete Kaddish after Hoshanot");
        }
        const musafDay = isRoshChodesh || isCholHamoed;
        if (musafDay) {
          add("song-of-the-day");
          if (rules.ledovid) add("ledovid-hashem-ori", "From Rosh Chodesh Elul through Hoshana Rabbah");
          add("mourner-s-kaddish", "After the Song of the Day");
        }
        if (torahReading) {
          if (rules.tachnun && [1, 4].includes(rules.weekday)) add("kel-erech-apayim", "Before opening the Ark");
          add("weekday-torah-service-opening");
          const readingReason = dailyTorah ? `${dailyTorah.name} · ${dailyTorah.summary}` : "Today’s Torah reading";
          const readingPresentation = dailyTorah ? {
            title: `Torah Reading — ${dailyTorah.name}`,
            hebrew: dailyTorah.hebrew || "",
          } : null;
          if (isChanukah) add("torah-reading-chanukah", readingReason, readingPresentation);
          else if (isPurim) add("torah-reading-purim", readingReason, readingPresentation);
          else if (isRoshChodesh) add("torah-reading-rosh-chodesh", readingReason, readingPresentation);
          else if (isCholHamoed && isPesach) add("torah-reading-chol-hamoed-pesach", readingReason, readingPresentation);
          else if (isCholHamoed && isSukkot) add("torah-reading-chol-hamoed-sukkot", readingReason, readingPresentation);
          else if (isPublicFast) add("torah-reading-public-fast-days", readingReason, readingPresentation);
          else add("torah-reading-weekly-portions", readingReason, readingPresentation);
          add("weekday-torah-service-conclusion", "Concluding aliyah blessing, Half Kaddish, optional Mi Sheberach prayers, Hagbah, and Gelilah");
        }
        if (isPurim) add("megillat-esther", "Complete Megillah reading before Ashrei and Uva Letzion");
        add("shacharit-conclusion", torahReading ? "Ashrei and Uva Letzion before returning the Torah" : "Ashrei, Lamnatzeach, Uva Letzion, and Complete Kaddish");
        if (!musafDay) add("shacharit-complete-kaddish");
        if (torahReading) add("returning-the-torah");
        if (isRoshChodesh) add("rabbeinu-tam-s-tefillin", "Before Musaf on Rosh Chodesh");
        if (isRoshChodesh) add("musaf-for-weekday-rosh-chodesh");
        if (isCholHamoed) add("musaf-for-chol-hamoed");
        if (musafDay) add("shacharit-complete-kaddish");
        if (!musafDay) add("post-musaf-conclusion");
        if (!musafDay) {
          add("song-of-the-day");
          if (rules.ledovid) add("ledovid-hashem-ori", "From Rosh Chodesh Elul through Hoshana Rabbah");
          add("mourner-s-kaddish", "After the Song of the Day");
        }
        add("ein-kelokeinu");
        add("kaddish-d-rabbanan-167", "After Ein Kelokeinu");
        add("aleinu");
        add("daily-tehillim", rules.hdateMonth === 6 || (rules.hdateMonth === 7 && rules.hdateDay <= 10)
          ? "Today’s monthly Tehillim portion and Elul–Tishrei addition"
          : "Today’s monthly Tehillim portion");
        if (!isRoshChodesh && !isCholHamoed) add("rabbeinu-tam-s-tefillin");
        add("six-remembrances");
        if (nassiDay) add("nassi", `Nassi for ${hdate.getDate()} Nisan`);
        add("hayom-yom", "Today’s daily Hayom Yom study");
      }
    } else if (period.id === "afternoon") {
      if (weekday === 5) add("minchah-for-friday", "Friday introduction");
      if (isPublicFast) {
        add("minchah-public-fast-opening", "Korbanot, Ashrei, and Half Kaddish");
        add("weekday-torah-service-opening", "Opening the Ark and blessings before the public-fast Torah reading");
        add(
          "torah-reading-public-fast-days",
          dailyTorah ? `${dailyTorah.name} · ${dailyTorah.summary} · Haftarah ${dailyTorah.haftara}` : "Public-fast Minchah Torah reading and Haftarah",
          dailyTorah ? { title: `Torah Reading and Haftarah — ${dailyTorah.name}`, hebrew: dailyTorah.hebrew || "" } : null,
        );
        if (!isTishaBAv && rules.tachnun) {
          add("minchah-public-fast-through-tachnun", "After returning the Torah and Half Kaddish; through Tachanun");
          add("the-long-avinu-malkeinu", "Public fast; before Va’anachnu Lo Neida and Complete Kaddish");
          add("minchah-public-fast-after-avinu", "Va’anachnu Lo Neida, Complete Kaddish, Psalm 27 when applicable, and Aleinu");
        } else {
          add("minchah-public-fast-amidah-and-conclusion", "After returning the Torah and Half Kaddish");
        }
      } else {
        const longAvinuAtMinchah = tenDays && hdate.getDate() <= 8 && weekday !== 5;
        if (longAvinuAtMinchah) {
          add("minchah-weekday-through-tachnun", "Through Tachanun");
          add("the-long-avinu-malkeinu", "Ten Days of Repentance; before Va’anachnu Lo Neida and Complete Kaddish");
          add("minchah-weekday-after-avinu", "Va’anachnu Lo Neida, Complete Kaddish, Psalm 27, and Aleinu");
        } else {
          add("minchah-weekday", weekday === 5 ? "Continue with the weekday Minchah order" : "");
        }
      }
    } else {
      const insertionBeforeAleinu = isPurim || isTishaBAv || rules.omer;
      add(insertionBeforeAleinu ? "maariv-weeknights-through-kaddish" : "maariv-weeknights");
      if (isPurim) add("megillat-esther", "Complete Megillah reading before Aleinu");
      if (isTishaBAv) {
        add("megillat-eichah", "Megillat Eichah before Aleinu");
        add("kinot-tisha-bav-evening", "Evening Kinot after Megillat Eichah and before Aleinu");
      }
      if (rules.omer) add("counting-the-omer", "Before Aleinu");
      if (insertionBeforeAleinu) add("maariv-weeknights-aleinu");
      if (afterYomKippur) {
        add("yom-kippur-havdalah", "After Maariv at the conclusion of Yom Kippur", null, SERVICE_SOURCES.yomKippur);
      }
      add("prayer-before-retiring-at-night");
    }
  }

  if (shabbat && !sacredDay) {
    if (period.id === "evening") {
      add("kabbalat-shabbat");
      add("shabbat-maariv");
      if (rules.omer) add("counting-the-omer");
      add("prayer-before-retiring-at-night");
    } else if (period.id === "morning") {
      add("morning-blessings");
      add("order-of-putting-on-the-tzitzit");
      add("order-of-putting-on-the-tallit");
      add("morning-prayer");
      add("kaddish-d-rabbanan", "After Korbanot");
      add("hodu");
      add("shabbat-pesukei-dezimra");
      add("shabbat-blessings-of-shema");
      add("shabbat-shacharit-amidah");
      if (hallel) add("hallel", fullHallel ? "Complete Hallel" : "Half Hallel");
      add("shabbat-song-of-the-day");
      add("shabbat-torah-service-opening");
      add(
        "torah-reading-weekly-portions",
        dailyTorah ? `${dailyTorah.name} · ${dailyTorah.summary}${dailyTorah.haftara ? ` · Haftarah ${dailyTorah.haftara}` : ""}` : "This Shabbat’s Torah reading",
        dailyTorah ? { title: `Torah Reading — ${dailyTorah.name}`, hebrew: dailyTorah.hebrew || "" } : null,
      );
      add("shabbat-torah-service-conclusion");
      add(isCholHamoed ? "festival-musaf-amidah" : "shabbat-musaf", isCholHamoed ? "Shabbat Chol Hamoed Musaf" : "");
      add("aleinu");
      if (rules.ledovid) add("ledovid-hashem-ori", "Through Hoshana Rabbah");
      add("daily-tehillim", rules.hdateMonth === 6 || (rules.hdateMonth === 7 && rules.hdateDay <= 10)
        ? "Today’s monthly Tehillim portion and Elul–Tishrei addition"
        : "Today’s monthly Tehillim portion");
      add("six-remembrances");
      if (nassiDay) add("nassi", `Nassi for ${hdate.getDate()} Nisan`);
      add("hayom-yom", "Today’s daily Hayom Yom study");
    } else {
      add("shabbat-minchah-opening");
      add(
        "torah-reading-weekly-portions",
        dailyTorah ? `${dailyTorah.name} · ${dailyTorah.summary}` : "The opening of the coming week’s Torah portion",
        dailyTorah ? { title: `Torah Reading — ${dailyTorah.name}`, hebrew: dailyTorah.hebrew || "" } : null,
      );
      add("shabbat-minchah-conclusion");
      if (rules.ledovid) add("ledovid-hashem-ori", "Through Hoshana Rabbah");
    }
  }

  const dateKey = `${hdate.getFullYear()}-${hdate.getMonth()}-${hdate.getDate()}`;
  const variant = {
    observance: il ? "israel" : "diaspora",
    service: period.id,
    shabbat,
    occasion: isYomKippur ? "yom-kippur" : isRoshHashanah ? "rosh-hashanah"
      : isSimchatTorah ? "simchat-torah" : isSheminiAtzeret ? "shemini-atzeret"
        : isSukkot ? "sukkot" : isShavuot ? "shavuot" : isPesach ? "pesach"
          : isCholHamoed ? "chol-hamoed" : isRoshChodesh ? "rosh-chodesh"
            : isChanukah ? "chanukah" : isPurim ? "purim"
              : isPublicFast ? "public-fast" : "ordinary",
  };
  const hasSection = (id) => sections.some((entry) => entry.id === id);
  const conditionalPassages = [
    `tachnun:${period.id === "evening" ? "not-applicable" : rules.tachnun ? "said" : `omitted:${rules.tachnunReason || "calendar"}`}`,
    `hallel:${!hallel ? "none" : fullHallel ? "complete" : "half"}`,
    `tefillin:${hasSection("order-of-putting-on-the-tefillin") ? "regular" : hasSection("rabbeinu-tam-s-tefillin") ? "rabbeinu-tam-only" : "none"}`,
    `ledovid:${rules.ledovid ? "said" : "omitted"}`,
    `nassi:${nassiDay ? hdate.getDate() : "none"}`,
    `omer:${rules.omer ? "count" : "none"}`,
    `selichot:${sections.find((entry) => entry.id.startsWith("selichot"))?.id || "none"}`,
    `avinu-malkeinu:${hasSection("the-long-avinu-malkeinu") || hasSection("rosh-hashanah-amidah") || hasSection("yom-kippur-amidah") ? "said" : "none"}`,
    `hoshanot:${hasSection("hoshanot") ? `days-${hoshanotDays.join("+")}` : "none"}`,
    `torah:${dailyTorah?.name || (torahReading ? "scheduled" : "none")}`,
    `haftarah:${dailyTorah?.haftara || "none"}`,
    `musaf:${sections.find((entry) => /musaf/.test(entry.id))?.id || "none"}`,
    `yizkor:${hasSection("festival-yizkor") ? "said" : "none"}`,
    `hakafot:${hasSection("simchat-torah-hakafot") ? "said" : "none"}`,
    `kol-nidrei:${hasSection("yom-kippur-kol-nidrei") ? "said" : "none"}`,
    `neilah:${hasSection("yom-kippur-neilah") ? "said" : "none"}`,
    `amidah-season:morid-hageshem=${rules.moridHageshem ? "yes" : "no"};tal-umatar=${rules.talUmatar ? "yes" : "no"};ten-days=${rules.tenDays ? "yes" : "no"}`,
    "minyan-roles:individual+congregation+chazzan-labeled",
  ];
  const canonicalVariant = [variant.observance, variant.service, variant.occasion, variant.shabbat ? "shabbat" : "weekday"].join(":");
  const renderedSignature = `${sections.map((entry) => entry.semanticId).join(" > ")} || ${conditionalPassages.join(" | ")}`;
  return {
    id: `${dateKey}:${period.id}:${il ? "il" : "diaspora"}:v3`,
    dateKey,
    service: period.id,
    title: period.title,
    hebrew: period.hebrew,
    sections,
    sectionIds: sections.map((entry) => entry.id),
    variant,
    canonicalVariant,
    conditionalPassages,
    renderedSignature,
    signature: renderedSignature,
    signatureVersion: 3,
    sourceReferences: [...sourceReferences],
    sourceMetadata: sections.filter((entry) => entry.sourceReference).map((entry) => ({
      semanticId: entry.semanticId,
      sourceReference: entry.sourceReference,
    })),
    homeItems: additions,
    rules,
    supported: sections.length > 0,
    unavailableReason: sections.length ? "" : period.note || "This service is not included in the supplied edition.",
    sourceModel: "semantic rules validated against the installed Tehillat Hashem schedule model",
  };
}

function currentPeriod(instant, zmanim) {
  const dawn = zmanim.alosBaalHatanya();
  const minchah = zmanim.minchaGedolaBaalHatanya();
  const sunset = zmanim.shkiah();
  if (instant < dawn || instant >= sunset) return PERIODS.evening;
  if (instant >= minchah) return PERIODS.afternoon;
  return PERIODS.morning;
}

function nextTransition(period, zmanim, timeZone, instant, location, targetDate) {
  let value;
  let label;
  if (period.id === "morning") {
    value = zmanim.minchaGedolaBaalHatanya();
    label = "Minchah begins";
  } else if (period.id === "afternoon") {
    value = zmanim.shkiah();
    label = "Maariv begins after sunset";
  } else {
    value = zmanim.alosBaalHatanya();
    if (value <= instant) {
      const tomorrow = new Date(targetDate);
      tomorrow.setDate(tomorrow.getDate() + 1);
      value = new Zmanim(location, tomorrow).alosBaalHatanya();
    }
    label = "Shacharit begins after dawn";
  }
  return { label, time: formatTime(value, timeZone), at: value.toISOString() };
}

function selichotServiceForDate(hdate) {
  if (hdate.getMonth() !== months.ELUL || hdate.greg().getDay() === 6) return null;
  const roshHashana = new HDate(1, months.TISHREI, hdate.getFullYear() + 1);
  const weekday = roshHashana.greg().getDay();
  let daysToPreviousSaturday = (weekday - 6 + 7) % 7 || 7;
  if (weekday === 1 || weekday === 2) daysToPreviousSaturday += 7;
  const firstSelichot = roshHashana.abs() - daysToPreviousSaturday + 1;
  if (hdate.abs() < firstSelichot) return null;
  if (hdate.getDate() === 29) {
    return {
      section: "selichot-erev-rosh-hashanah",
      title: "Selichot - Erev Rosh Hashanah",
      hebrew: "סְלִיחוֹת לְעֶרֶב רֹאשׁ הַשָּׁנָה",
    };
  }
  let serviceDay = 0;
  for (let absoluteDay = firstSelichot; absoluteDay <= hdate.abs(); absoluteDay += 1) {
    if (new HDate(absoluteDay).greg().getDay() !== 6) serviceDay += 1;
  }
  if (serviceDay < 1 || serviceDay > 7) return null;
  const dayNames = ["First Night", "Day Two", "Day Three", "Day Four", "Day Five", "Day Six", "Day Seven"];
  const hebrewDays = ["רִאשׁוֹן", "שֵׁנִי", "שְׁלִישִׁי", "רְבִיעִי", "חֲמִישִׁי", "שִׁשִּׁי", "שְׁבִיעִי"];
  return {
    section: `selichot-day-${serviceDay}`,
    title: `Selichot - ${dayNames[serviceDay - 1]}`,
    hebrew: `סְלִיחוֹת לְיוֹם ${hebrewDays[serviceDay - 1]}`,
  };
}

function isSelichotSeason(hdate) {
  return Boolean(selichotServiceForDate(hdate));
}

function isKiddushLevanaWindow(instant, hdate) {
  const molad = getMoladAsDate(calculateMolad(hdate.getFullYear(), hdate.getMonth()));
  const moladMs = Number(molad.epochMilliseconds);
  const start = moladMs + 7 * 86400000;
  const end = moladMs + (14 * 24 * 60 + 18 * 60 + 22) * 60000;
  if (hdate.getMonth() === months.AV && hdate.getDate() <= 9) return false;
  return hdate.getDate() <= 14 && instant.valueOf() >= start && instant.valueOf() <= end;
}

function buildAdditions({ hdate, instant, period, holidayEvents, timedEvents, weekday, il, shabbat, sacredDay }) {
  const additions = [];
  const notices = [];
  const observedEvents = holidayEvents.filter((event) => !/^Erev /.test(event.getDesc()));
  const names = observedEvents.map((event) => event.getDesc());
  const basenames = observedEvents.map((event) => event.basename());
  const masks = observedEvents.reduce((mask, event) => mask | event.getFlags(), 0);
  const isFast = Boolean(masks & (flags.MINOR_FAST | flags.MAJOR_FAST));
  const isPublicFast = isFast && !names.some((name) => /Ta'anit Bechorot|Yom Kippur Katan/i.test(name));
  const isTishaBAv = names.some((name) => /Tish.?a B.?Av/i.test(name));
  const isRoshChodesh = Boolean(masks & flags.ROSH_CHODESH);
  const isCholHamoed = Boolean(masks & flags.CHOL_HAMOED);
  const chanukahDay = hdate.abs() - new HDate(25, months.KISLEV, hdate.getFullYear()).abs() + 1;
  const isChanukah = basenames.includes("Chanukah") && chanukahDay >= 1 && chanukahDay <= 8;
  const isPurim = basenames.includes("Purim") && !names.some((name) => name.includes("Katan"));
  const isRoshHashanah = basenames.includes("Rosh Hashana");
  const isPesach = basenames.includes("Pesach");
  const isSukkot = basenames.includes("Sukkot");
  const omer = holidayEvents.find((event) => event.getFlags() & flags.OMER_COUNT);

  for (const event of holidayEvents) {
    const mask = event.getFlags();
    if (mask & (flags.CHAG | flags.ROSH_CHODESH | flags.MINOR_FAST | flags.MAJOR_FAST | flags.CHOL_HAMOED | flags.MINOR_HOLIDAY)) {
      const name = event.render("en");
      if (!notices.includes(name) && !/^Erev /.test(name)) notices.push(name);
    }
  }

  if (omer) {
    addUnique(additions, item("counting-the-omer", omer.render("en"), omer.render("he"), "Count after nightfall; shown throughout the applicable Hebrew date.", "seasonal"));
  }

  if (isRoshChodesh) {
    addUnique(additions, item("hallel", "Hallel", "הַלֵּל", "Rosh Chodesh"));
    if (weekday !== 6) {
      addUnique(additions, item("torah-reading-rosh-chodesh", "Torah Reading for Rosh Chodesh", "קְרִיאַת הַתּוֹרָה לְרֹאשׁ חֹדֶשׁ", "Rosh Chodesh"));
      addUnique(additions, item("musaf-for-weekday-rosh-chodesh", "Musaf for Rosh Chodesh", "מוּסָף לְרֹאשׁ חֹדֶשׁ", "Weekday Rosh Chodesh"));
    }
  }

  if (isChanukah) {
    addUnique(additions, item("hoshanot", "Kindling the Chanukah Lights", "הַדְלָקַת נֵרוֹת חֲנֻכָּה", "Chanukah", "seasonal", "the-order-of-kindling-the-chanukah-lights"));
    addUnique(additions, item("hallel", "Complete Hallel", "הַלֵּל שָׁלֵם", "Chanukah"));
    addUnique(additions, item("torah-reading-chanukah", "Torah Reading for Chanukah", "קְרִיאַת הַתּוֹרָה לַחֲנֻכָּה", "Chanukah"));
  }

  if (isPurim) {
    addUnique(additions, item("megillat-esther", "Megillat Esther", "מְגִלַּת אֶסְתֵּר", period.id === "evening" ? "Purim night" : "Purim day", "seasonal"));
    addUnique(additions, item("hoshanot", "Purim Prayers", "תְּפִלּוֹת פּוּרִים", "Purim", "seasonal", "purim"));
    if (period.id === "morning") addUnique(additions, item("torah-reading-purim", "Torah Reading for Purim", "קְרִיאַת הַתּוֹרָה לְפוּרִים", "Purim morning"));
  }

  if (isCholHamoed) {
    addUnique(additions, item("hallel", "Hallel", "הַלֵּל", "Chol Hamoed"));
    addUnique(additions, item("musaf-for-chol-hamoed", "Musaf for Chol Hamoed", "מוּסָף לְחוֹל הַמּוֹעֵד", "Chol Hamoed"));
    if (isPesach) addUnique(additions, item("torah-reading-chol-hamoed-pesach", "Torah Reading for Chol Hamoed Pesach", "קְרִיאַת הַתּוֹרָה לְחוֹל הַמּוֹעֵד פֶּסַח", "Chol Hamoed Pesach"));
    if (isSukkot) {
      addUnique(additions, item("hoshanot", "Hoshanot", "הוֹשַׁעְנוֹת", "Chol Hamoed Sukkot"));
      addUnique(additions, item("torah-reading-chol-hamoed-sukkot", "Torah Reading for Chol Hamoed Sukkot", "קְרִיאַת הַתּוֹרָה לְחוֹל הַמּוֹעֵד סֻכּוֹת", "Chol Hamoed Sukkot"));
    }
  }

  if (isPublicFast) {
    if (isTishaBAv && period.id === "morning") {
      addUnique(additions, {
        section: "tisha-bav-morning",
        heading: "torah-reading-and-haftarah-for-tishah-bav-morning",
        title: "Tisha B’Av morning order",
        hebrew: "סֵדֶר שַׁחֲרִית לְתִשְׁעָה בְּאָב",
        reason: "Weekday Shacharit with the dedicated Torah reading, Haftarah, and complete daytime Kinot sequence.",
        type: "addition",
      });
    } else if (isTishaBAv && period.id === "evening") {
      addUnique(additions, item("megillat-eichah", "Megillat Eichah", "מְגִלַּת אֵיכָה", "Tishah B'Av night", "seasonal"));
      addUnique(additions, item("kinot-tisha-bav-evening", "Kinot for Tishah B'Av Evening", "קִינוֹת לְלֵיל תִּשְׁעָה בְּאָב", "After Megillat Eichah", "seasonal"));
    } else if (period.id !== "evening") {
      addUnique(additions, item("torah-reading-public-fast-days", "Torah Reading for a Public Fast", "קְרִיאַת הַתּוֹרָה לְתַעֲנִית צִבּוּר", names.find((name) => /Av|Fast|Tevet|Tammuz|Gedaliah|Esther/.test(name)) || "Public fast"));
    }
    if (!isTishaBAv) addUnique(additions, item("the-long-avinu-malkeinu", "Avinu Malkeinu", "אָבִינוּ מַלְכֵּנוּ", "Public fast"));
    if (names.some((name) => /Asara B'Tevet|10th of Tevet/i.test(name))) addUnique(additions, item("for-the-tenth-of-tevet", "Selichot for the Tenth of Tevet", "סְלִיחוֹת לַעֲשָׂרָה בְּטֵבֵת", "Tenth of Tevet"));
    if (names.some((name) => /Esther/i.test(name))) addUnique(additions, item("for-the-fast-of-esther", "Selichot for the Fast of Esther", "סְלִיחוֹת לְתַעֲנִית אֶסְתֵּר", "Fast of Esther"));
    if (names.some((name) => /Tammuz/i.test(name))) addUnique(additions, item("for-the-seventeenth-of-tammuz", "Selichot for the Seventeenth of Tammuz", "סְלִיחוֹת לְשִׁבְעָה עָשָׂר בְּתַמּוּז", "Seventeenth of Tammuz"));
    if (period.id === "morning" && names.some((name) => /Gedaliah/i.test(name))) {
      addUnique(additions, item("selichot-tzom-gedaliah", "Selichot - Tzom Gedaliah", "סְלִיחוֹת לְצוֹם גְּדַלְיָה", "Tzom Gedaliah", "seasonal"));
    }
  }

  if (isRoshHashanah && period.id === "afternoon") {
    addUnique(additions, item("tashlich", "The Order of Tashlich", "סֵדֶר תַּשְׁלִיךְ", "Rosh Hashanah afternoon", "seasonal"));
  }

  if (hdate.getMonth() === months.NISAN && hdate.getDate() >= 1 && hdate.getDate() <= 13) {
    addUnique(additions, item("nassi", `Nassi — ${hdate.getDate()} Nisan`, "סֵדֶר אֲמִירַת הַנָּשִׂיא", "The first thirteen days of Nisan", "seasonal"));
  }
  if (hdate.getMonth() === months.NISAN && hdate.getDate() === 14) {
    addUnique(additions, item("order-of-the-search-for-chametz", "Search and Removal of Chametz", "בְּדִיקַת וּבִיעוּר חָמֵץ", "Erev Pesach", "seasonal"));
    if (period.id === "afternoon") addUnique(additions, item("order-of-the-passover-offering", "Order of the Pesach Offering", "סֵדֶר קָרְבַּן פֶּסַח", "Erev Pesach afternoon", "seasonal"));
  }
  if (hdate.getMonth() === months.ELUL && hdate.getDate() === 29) {
    addUnique(additions, item("annulment-of-vows", "Annulment of Vows", "הַתָּרַת נְדָרִים", "Erev Rosh Hashanah", "seasonal"));
  }
  const selichotService = selichotServiceForDate(hdate);
  if (selichotService) {
    addUnique(additions, item(selichotService.section, selichotService.title, selichotService.hebrew, "The applicable complete Nusach Chabad Selichot service", "seasonal"));
  }
  if (hdate.getMonth() === months.TISHREI && hdate.getDate() >= 3 && hdate.getDate() <= 9) {
    addUnique(additions, item("kapparot", "Kapparot and Viduy", "כַּפָּרוֹת וְוִדּוּי", "Preparation during the Ten Days of Repentance", "seasonal"));
  }
  if (HebrewCalendar.eruvTavshilin(hdate, il)) {
    addUnique(additions, item("eruv-tavshilin", "Eruv Tavshilin", "עֵירוּב תַּבְשִׁילִין", "A festival leads directly into Shabbat", "seasonal"));
  }

  if (period.id === "evening" && isKiddushLevanaWindow(instant, hdate)) {
    addUnique(additions, item("sanctification-of-the-moon", "Sanctification of the Moon", "קִדּוּשׁ לְבָנָה", "The customary monthly window; recite only when conditions permit.", "seasonal"));
  }
  if (period.id === "evening" && !shabbat && !sacredDay) {
    addUnique(additions, item("prayer-before-retiring-at-night", "Prayer Before Retiring at Night", "קְרִיאַת שְׁמַע שֶׁעַל הַמִּטָּה", "Nighttime prayer", "daily"));
  }

  for (const event of timedEvents) {
    const desc = event.getDesc();
    if (desc === "Candle lighting") {
      addUnique(additions, { ...item("blessings-for-candle-lighting", "Candle Lighting", "הַדְלָקַת נֵרוֹת", `${event.render("en")}`, "timed"), time: event.fmtTime || event.eventTimeStr });
    } else if (desc === "Havdalah" && period.id === "evening") {
      addUnique(additions, { ...item("blessings-for-candle-lighting", "Havdalah", "הַבְדָּלָה", `${event.render("en")}`, "timed", "havdalah"), time: event.fmtTime || event.eventTimeStr });
    }
  }

  return { additions, notices };
}

export function getDailyInfo(config = {}, instant = new Date()) {
  const location = resolveLocation(config);
  const timeZone = location.getTzid();
  const parts = locationDateParts(instant, timeZone);
  const targetDate = new Date(parts.year, parts.month - 1, parts.day);
  const zmanim = new Zmanim(location, targetDate);
  const hdate = Zmanim.makeSunsetAwareHDate(location, instant, false);
  const weekday = hdate.greg().getDay();
  const periodBase = currentPeriod(instant, zmanim);
  const currentNight = periodBase.id === "evening";
  const afterCivilMidnightBeforeDawn = instant < zmanim.alosBaalHatanya();

  const holidayEvents = HebrewCalendar.calendar({
    start: hdate,
    end: hdate,
    il: location.getIsrael(),
    omer: true,
    sedrot: true,
    noModern: true,
    yomKippurKatan: true,
  });
  const timedEvents = HebrewCalendar.calendar({
    start: targetDate,
    end: targetDate,
    il: location.getIsrael(),
    location,
    candlelighting: true,
    noModern: true,
  }).filter((event) => event.eventTime instanceof Date);

  const civilHdate = new HDate(targetDate);
  const daysUntilShabbat = (6 - civilHdate.greg().getDay() + 7) % 7;
  const weeklyLeyning = getLeyningOnDate(new HDate(civilHdate.abs() + daysUntilShabbat), location.getIsrael());
  const weeklyAliyot = weeklyLeyning?.fullkriyah;
  const weeklyTorah = weeklyAliyot ? {
    name: weeklyLeyning.name.en,
    hebrew: weeklyLeyning.name.he,
    type: weeklyLeyning.type,
    summary: makeLeyningSummary(weeklyAliyot),
    ...(weeklyLeyning.haftara ? { haftara: weeklyLeyning.haftara, haft: weeklyLeyning.haft } : {}),
    aliyot: Object.entries(weeklyAliyot)
      .filter(([key]) => /^\d+$/.test(key))
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([number, aliyah]) => ({ number: Number(number), book: aliyah.k, start: aliyah.b, end: aliyah.e })),
  } : null;
  const dayPlans = Object.values(PERIODS).map((basePeriod) => {
    // The installed Siddur's daily schedule is a civil-day schedule: Shacharit
    // and Minchah belong to the Hebrew date during daylight, while Maariv
    // belongs to the Hebrew date that begins that evening. During Shacharit or
    // Minchah this is the upcoming night, but while Maariv is current it is the
    // sunset-aware Hebrew date already in progress. This distinction matters
    // after civil midnight: 2 AM Friday still belongs to Thursday-night
    // Maariv, and 2 AM Saturday still belongs to Friday-night Shabbat Maariv.
    const serviceHdate = basePeriod.id === "evening"
      ? currentNight ? hdate : new HDate(civilHdate.abs() + 1)
      : civilHdate;
    // A post-midnight Maariv also inherits timed events from the civil date on
    // which that night began (not from the coming evening). This preserves
    // Havdalah after midnight and prevents the next Friday's candle lighting
    // from leaking into Thursday-night Maariv.
    const serviceTimedEvents = basePeriod.id === "evening" && afterCivilMidnightBeforeDawn
      ? HebrewCalendar.calendar({
        start: new Date(parts.year, parts.month - 1, parts.day - 1),
        end: new Date(parts.year, parts.month - 1, parts.day - 1),
        il: location.getIsrael(),
        location,
        candlelighting: true,
        noModern: true,
      }).filter((event) => event.eventTime instanceof Date)
      : timedEvents;
    const serviceWeekday = serviceHdate.greg().getDay();
    const serviceHolidayEvents = HebrewCalendar.calendar({
      start: serviceHdate,
      end: serviceHdate,
      il: location.getIsrael(),
      omer: true,
      sedrot: true,
      noModern: true,
      yomKippurKatan: true,
    });
    const serviceShabbat = serviceWeekday === 6;
    const serviceSacredDay = isSacredDay(serviceHolidayEvents);
    const servicePeriod = { ...basePeriod };
    if (servicePeriod.id === "afternoon" && serviceWeekday === 5) servicePeriod.section = "minchah-for-friday";
    if (serviceSacredDay) {
      const occasion = serviceHolidayEvents.find((event) => (
        event.getFlags() & flags.CHAG
      ))?.basename();
      servicePeriod.title = `${occasion || "Yom Tov"} ${basePeriod.title}`;
      servicePeriod.hebrew = "תְּפִלּוֹת יוֹם טוֹב";
      servicePeriod.section = basePeriod.id === "morning" ? "morning-blessings"
        : basePeriod.id === "afternoon" ? "festival-minchah-opening" : "festival-maariv-opening";
      servicePeriod.note = "";
    } else if (serviceShabbat) {
      servicePeriod.title = servicePeriod.id === "morning" ? "Shabbat Shacharit"
        : servicePeriod.id === "afternoon" ? "Shabbat Minchah" : "Kabbalat Shabbat & Maariv";
      servicePeriod.hebrew = "תְּפִלּוֹת שַׁבָּת";
      servicePeriod.section = servicePeriod.id === "morning" ? "morning-blessings"
        : servicePeriod.id === "afternoon" ? "shabbat-minchah-opening" : "kabbalat-shabbat";
      servicePeriod.note = "";
    }
    const serviceContext = buildAdditions({
      hdate: serviceHdate,
      instant,
      period: servicePeriod,
      holidayEvents: serviceHolidayEvents,
      timedEvents: serviceTimedEvents,
      weekday: serviceWeekday,
      il: location.getIsrael(),
      shabbat: serviceShabbat,
      sacredDay: serviceSacredDay,
    });
    const servicePlan = buildDailyPrayerPlan({
      hdate: serviceHdate,
      period: servicePeriod,
      weekday: serviceWeekday,
      il: location.getIsrael(),
      shabbat: serviceShabbat,
      sacredDay: serviceSacredDay,
      holidayEvents: serviceHolidayEvents,
      timedEvents: serviceTimedEvents,
      additions: serviceContext.additions,
      dateParts: parts,
    });
    const orderedAdditions = orderAdditionsByServicePlan(
      serviceContext.additions,
      servicePlan.sections,
    );
    return {
      service: basePeriod.id,
      period: servicePeriod,
      plan: servicePlan,
      additions: orderedAdditions,
      notices: serviceContext.notices,
      current: basePeriod.id === periodBase.id,
    };
  });
  const currentSchedule = dayPlans.find((entry) => entry.current) || dayPlans[0];
  const { period, plan, additions, notices } = currentSchedule;
  const unavailable = !plan.supported;
  const gregorian = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone }).format(instant);
  const offset = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortOffset" }).formatToParts(instant).find((part) => part.type === "timeZoneName")?.value || "";
  const zoneName = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }).formatToParts(instant).find((part) => part.type === "timeZoneName")?.value || "";
  const values = [
    ["Dawn", "Alot HaShachar", "dawn", zmanim.alosBaalHatanya()],
    ["Earliest Tallit & Tefillin", "Misheyakir", "tallit", zmanim.misheyakir()],
    ["Sunrise", "Hanetz HaChamah", "sunrise", zmanim.neitzHaChama()],
    ["Latest Shema", "Sof Zman Shema", "shema", zmanim.sofZmanShmaBaalHatanya()],
    ["Latest Shacharit", "Sof Zman Tefillah", "shacharit", zmanim.sofZmanTfilaBaalHatanya()],
    ["Midday", "Chatzot Hayom", "midday", zmanim.chatzot()],
    ["Earliest Minchah", "Minchah Gedolah", "minchah", zmanim.minchaGedolaBaalHatanya()],
    ["Minchah Ketanah", "Minchah Ketanah", "minchah", zmanim.minchaKetanaBaalHatanya()],
    ["Plag HaMinchah", "Plag HaMinchah", "plag", zmanim.plagHaminchaBaalHatanya()],
    ["Sunset", "Shkiah", "sunset", zmanim.shkiah()],
    ["Nightfall", "Tzeit HaKochavim", "night", zmanim.tzaisBaalHatanya()],
    ["Midnight", "Chatzot HaLailah", "midnight", zmanim.chatzotNight()],
  ].map(([label, transliteration, icon, value]) => ({ label, transliteration, icon, value: value.toISOString(), time: formatTime(value, timeZone) }));
  const baalSunrise = zmanim.getSunriseBaalHatanya();
  const baalSunset = zmanim.getSunsetBaalHatanya();

  return {
    location: { name: location.getName(), latitude: location.getLatitude(), longitude: location.getLongitude(), tzid: timeZone, il: location.getIsrael() },
    gregorian,
    hebrew: hdate.render("en"),
    hebrewGematriya: hdate.renderGematriya(),
    hebrewDay: hdate.getDate(),
    hebrewMonth: hdate.getMonth(),
    hebrewYear: hdate.getFullYear(),
    hebrewDaysInMonth: hdate.daysInMonth(),
    weeklyTorah,
    offset,
    zoneName,
    values,
    shaahZmanitMinutes: Math.round(((baalSunset.epochMilliseconds - baalSunrise.epochMilliseconds) / 12 / 60000) * 10) / 10,
    method: "Baal HaTanya",
    smart: {
      period,
      unavailable,
      additions,
      notices,
      plan,
      dayPlans,
      next: nextTransition(periodBase, zmanim, timeZone, instant, location, targetDate),
      events: holidayEvents.map((event) => eventSummary(event, timeZone)),
      timedEvents: timedEvents.map((event) => eventSummary(event, timeZone)),
      sourceModel: "time period + Hebrew date + Israel/Diaspora observance",
    },
  };
}

export { CITY_NAMES, buildDailyPrayerPlan };
